import * as zod from 'zod';

import { SpeedProfile } from '@sb/routine-runner/speed-profile';

import { MillerWeldParameters } from '../../../MillerWeldMachine/types/MillerWeldMachineCommands';

/** Entered in seconds. The field steps by 0.1 but any value is accepted. */
export const ROTARY_WELD_MAX_DURATION_SECONDS = 500;

/**
 * What ends the weld. The ports themselves live on the positioner equipment,
 * so a step only chooses *which kind* of signal ends its weld — how long, or
 * the positioner saying so. A union rather than a `waitFor` enum beside an
 * optional duration, so a signal-based weld cannot carry a dangling duration.
 */
export const RotaryWeldCompletion = zod.discriminatedUnion('kind', [
  /** Wait for the positioner's completion sensor. */
  zod.object({ kind: zod.literal('positionerSignal') }),
  zod.object({
    kind: zod.literal('time'),
    /**
     * No `multipleOf`: the field steps by 0.1, but an operator who types 0.15
     * means it, and rejecting it here would fail the parse on reload rather
     * than at entry — the value is already saved by then.
     */
    durationSeconds: zod.number().gt(0).lte(ROTARY_WELD_MAX_DURATION_SECONDS),
  }),
]);

export type RotaryWeldCompletion = zod.infer<typeof RotaryWeldCompletion>;

/**
 * A weld where the positioner rotates the workpiece and the robot holds the
 * torch still, as opposed to `WeldArguments` where robot motion defines the
 * path. The robot position is the step's single waypoint substep, not an
 * argument.
 *
 * Travel speed, stitching and weaving have no analog here: nothing moves along
 * the joint while the arc is live.
 */
export const RotaryWeldArguments = zod.object({
  selectedMachineID: zod.string().optional(),
  selectedTorchID: zod.string().optional(),
  /**
   * The `WeldPositioner` equipment that turns the part. Its trigger port and
   * status ports are its own configuration: they describe the cell, not this
   * weld, and asking for them per step invites two steps to disagree about the
   * same hardware.
   */
  selectedPositionerID: zod.string().optional(),

  millerWeldParameters: MillerWeldParameters,

  craterFillTime: zod.number(), // how long to wait for crater fill
  arcStartTime: zod.number(), // how long to hold the arc before rotating

  approachSpeedProfile: SpeedProfile,

  completion: RotaryWeldCompletion,
  /**
   * Gate the arc on the positioner reporting aligned. The port and its active
   * level come from the positioner; this only says whether to check.
   */
  alignmentRequired: zod.boolean().default(false),
});

export type RotaryWeldArguments = zod.infer<typeof RotaryWeldArguments>;
